import insertParticipants from '$tablet-app/src/app/calendar/common/insertParticipants';
import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import Dialog from '$tablet-app/src/app/components/Dialog';
import { TaskListDetail } from '$tablet-app/src/app/components/TaskListDetail/TaskListDetail';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { postDeleteRecordLogEvent } from '$tablet-app/src/app/logging/functions';
import { MainDrawerScreenProps } from '$tablet-app/src/app/navigation/MainDrawerNavigator';
import { SectionIntroScreen } from '$tablet-app/src/app/screens/SectionIntroScreen';
import { showInfoToast, showSuccessToast } from '$tablet-app/src/app/toasts';
import {
  useCurrentUserID,
  useListCurrentHabitatUsers,
} from '$tablet-app/src/app/users';
import { InfoVideoName } from '$tablet-app/src/app/videos/types';
import { FeatureDomain, TaskListType, User } from '@akin-home/amplify';
import { Button } from '@akin-home/buttons/native';
import { ChecklistModel } from '@akin-home/checklists';
import {
  useDeleteTaskList,
  useListTaskListsByHabitat,
} from '@akin-home/checklists/frontend';
import { useHabitatID } from '@akin-home/habitats/react';
import { getId } from '@akin-home/records';
import { useGetSystemProperty } from '@akin-home/system-properties-react';
import { useIsFocused } from '@react-navigation/native';
import React, { useEffect, useMemo } from 'react';
import { ScrollView } from 'react-native';
import ChecklistsScreenView from './ChecklistsScreenView';

interface State {
  searchInput: string;
  filteredUserIDs: string[];
  openedList?: ChecklistModel | undefined;
}

const ChecklistsScreen = (props: MainDrawerScreenProps<'Tasks'>) => {
  const {
    navigation: { navigate },
  } = props;

  const is_focused = useIsFocused();

  const [state, setState] = React.useState<State>({
    searchInput: '',
    filteredUserIDs: [],
  });

  const user_id = useCurrentUserID();
  const habitatID = useHabitatID();
  const { data: users } = useListCurrentHabitatUsers();

  const { lists: data, isLoading } = useListTaskListsByHabitat({ habitatID });
  const { data: info_video } = useGetSystemProperty(
    InfoVideoName.CHECKLIST_SCREEN
  );

  const remove = useDeleteTaskList();

  useUpdateScreenChatContext();

  const onPressNewChecklist = (listType?: TaskListType) => {
    if (listType) {
      navigate('CreateChecklist', {
        defaults: { type: listType },
      });
    } else {
      navigate('CreateChecklist');
    }
  };

  const onChangeUserFilter = (users: User[]) =>
    setState({ ...state, filteredUserIDs: users.map(getId as any) });

  const onChangeSearchInput = (searchInput: string) =>
    setState({ ...state, searchInput });

  const onPressEditList = () => {
    if (state.openedList) {
      const { id } = state.openedList;
      navigate('EditChecklist', { id });
      closeList();
    }
  };

  const onDeleteTaskList = () => {
    if (!state.openedList) return;

    showInfoToast({
      text1: 'Deleting list...',
    });

    closeList();

    const id = state.openedList.id;

    remove(id).then(() => {
      postDeleteRecordLogEvent({
        user_id,
        feature_domain: FeatureDomain.CHECKLISTS,
        data: {
          id,
          model: 'Checklist',
        },
      });

      showSuccessToast({
        text1: 'Success!',
        text2: 'The list was deleted',
      });
    });
  };

  const closeList = () => setState({ ...state, openedList: undefined });

  const onCopyChecklist = () => {
    if (state.openedList) {
      navigate('CreateChecklist', { defaults: { ...state.openedList } });
      closeList();
    }
  };

  const items = useMemo(() => {
    const filtered_lists =
      state.searchInput !== '' || state.filteredUserIDs.length
        ? data.filter(
            filterCheckLists(state.searchInput, state.filteredUserIDs)
          )
        : data;

    return filtered_lists.map(insertParticipants(users));
  }, [data, state.searchInput, state.filteredUserIDs, users]);

  useEffect(() => {
    if (is_focused && !isLoading) {
      sendBackgroundMessage(intent_labels.open_checklists_screen);
    }
  }, [is_focused, isLoading]);

  if (!isLoading && !data.length && info_video?.value?.length) {
    return (
      <SectionIntroScreen
        screen_title="Task Lists"
        video_title="Watch the video and learn how to add your first checklist"
        source={{ drive_id: info_video.value }}
        action_button={
          <Button icon="plus" onPress={() => onPressNewChecklist()}>
            Add your first checklist
          </Button>
        }
      />
    );
  }

  return (
    <>
      <ChecklistsScreenView
        {...state}
        checklists={items}
        users={users}
        isLoading={isLoading}
        onPressNewChecklist={onPressNewChecklist}
        onChangeUserFilter={onChangeUserFilter}
        onChangeSearchInput={onChangeSearchInput}
      />

      <Dialog visible={!!state.openedList} onClose={closeList}>
        {state.openedList ? (
          <ScrollView>
            <TaskListDetail
              item={state.openedList}
              dismiss={closeList}
              remove={onDeleteTaskList}
              edit={onPressEditList}
              copy={onCopyChecklist}
              visible
            />
          </ScrollView>
        ) : null}
      </Dialog>

      <DeveloperScreenInfo />
    </>
  );
};

const filterCheckLists =
  (input: string, userIds: string[]) => (checkList: ChecklistModel) => {
    const should_filter_by_users = !!userIds.length;

    return (
      filterChecklistByText(input)(checkList) &&
      (!should_filter_by_users || filterChecklistByUsers(userIds)(checkList))
    );
  };

const filterChecklistByText = (text: string) => (checklist: ChecklistModel) => {
  return (
    filterChecklistByTitle(text)(checklist) ||
    filterChecklistByItemText(text)(checklist)
  );
};

const filterChecklistByTitle =
  (text: string) => (checklist: ChecklistModel) => {
    const title = checklist.title.toLocaleLowerCase();

    return title.includes(text.toLocaleLowerCase());
  };

const filterChecklistByItemText =
  (searchString: string) => (taskList: ChecklistModel) => {
    return taskList.checklist?.some((item) =>
      item.title.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
    );
  };

const filterChecklistByUsers =
  (user_ids: string[]) => (checklist: ChecklistModel) => {
    return (
      checklist.participantIDs.some((id) => user_ids.includes(id)) ||
      user_ids.includes(checklist.principalID)
    );
  };

export default ChecklistsScreen;
